import React, { useEffect, useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { format, parseISO, subDays } from 'date-fns';
import {
    useOverallMetrics,
    useEspPerformance,
    useTagDistribution,
    useCampaignHistory
} from '../hooks/useDashboardData';
import { dashboardApi } from '../services/dashboardApi';
import type { EspMetric, TagMetric, Campaign } from '../services/dashboardApi';
import SegmentationRulesConfig from '../components/SegmentationRulesConfig';
import IspPerformanceCharts from '../components/IspPerformanceCharts';
import { DeliverabilityPanel } from '../components/DeliverabilityPanel';
import { EspKpiCommandCenter } from '../components/EspKpiCommandCenter';


// Types
interface NewsletterStats {
    newsletterId: string;
    name: string;
    sendOn: string;
    recipients: number;
    uniqueOpened: number;
    openRate: number;
    hoursSinceSend: number;
    estimatedOpenRate?: number;
}

interface ChartDataPoint {
    date: string;
    actualOpens: number;
    estimatedOpens: number;
    totalEstimated: number;
    isRecent: boolean;
}

type TabType = 'kpis' | 'performance' | 'rules' | 'deliverability';

// Constants
const countries = [
    { id: 'NO', label: 'Norway' },
    { id: 'SE', label: 'Sweden' },
    { id: 'FI', label: 'Finland' },
    { id: 'DE', label: 'Germany' }
];

const systems = [
    { id: 'gr_se2', label: 'GetResponse', country: 'SE', esp: 'getresponse', campaignId: 'placeholder' },
    { id: 'ms_se', label: 'Moosend', country: 'SE', esp: 'moosend', campaignId: 'a922f87b-fa7c-424a-b84a-a49bc1452cfe' },
    { id: 'dp_se', label: 'Drip', country: 'SE', esp: 'drip', campaignId: '8284673' },
    { id: 'gr_fi', label: 'GetResponse', country: 'FI', esp: 'getresponse', campaignId: 'placeholder' },
    { id: 'ml_fi', label: 'MailerLite', country: 'FI', esp: 'mailerlite', campaignId: '181013946874987943' },
    { id: 'br_fi', label: 'Brevo', country: 'FI', esp: 'brevo', campaignId: '3' },
    { id: 'gr_de', label: 'GetResponse', country: 'DE', esp: 'getresponse', campaignId: 'placeholder' },
    { id: 'gr_no', label: 'GetResponse', country: 'NO', esp: 'getresponse', campaignId: 'Xk5x1' },
];

// Estimation parameters (from open_rate_estimator.py)
const DEFAULT_OR_MAX = 0.40; // 40% max open rate
const DEFAULT_K = 0.15;

/**
 * Exponential growth model for open rates - matches Python implementation
 */
function exponentialGrowthModel(t: number, orMax: number, k: number): number {
    return orMax * (1 - Math.exp(-k * t));
}

/**
 * Estimate final open rate at 72 hours based on current data
 * Matches the logic from open_rate_estimator.py
 */
function estimateFinalOpenRate(
    hoursElapsed: number,
    currentOpenRate: number,
    targetHours: number = 72.0
): number {
    let orMax = DEFAULT_OR_MAX;
    let k = DEFAULT_K;

    // Refine k based on current observation (same as Python implementation)
    if (currentOpenRate > 0 && hoursElapsed > 0) {
        const ratio = currentOpenRate / orMax;
        if (ratio < 0.99) {
            const kRefined = -Math.log(1 - ratio) / hoursElapsed;
            // Blend with historical k (weighted average)
            k = 0.7 * kRefined + 0.3 * k;
        }
    }

    // Predict open rate at target_hours
    let estimatedOpenRate = exponentialGrowthModel(targetHours, orMax, k);

    // Ensure estimate is reasonable
    estimatedOpenRate = Math.min(estimatedOpenRate, 1.0); // Cap at 100%
    estimatedOpenRate = Math.max(estimatedOpenRate, currentOpenRate); // Must be >= current

    return estimatedOpenRate;
}

export default function EspDashboard() {
    const [activeTab, setActiveTab] = useState<TabType>('kpis');
    const [showLegacyTabs, setShowLegacyTabs] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('NO');
    const [selectedEsp, setSelectedEsp] = useState('');
    const [loading, setLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateStatus, setUpdateStatus] = useState('');
    const [newsletters, setNewsletters] = useState<NewsletterStats[]>([]);
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

    // Date range for metrics (default: last 7 days)
    const [dateRange] = useState({
        days: 7,
        startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd')
    });

    // Map selectedCountry to geo code
    const geoCode = selectedCountry.toLowerCase(); // NO -> no

    // Fetch RDS metrics when a supported system is selected
    const selectedSystemObj = systems.find(s => s.id === selectedEsp);
    const isSystemSelected = !!selectedSystemObj;
    const espShortCode = selectedSystemObj?.esp === 'mailerlite' ? 'ml' : selectedSystemObj?.esp === 'moosend' ? 'ms' : selectedSystemObj?.esp === 'drip' ? 'dp' : selectedSystemObj?.esp === 'brevo' ? 'br' : 'gr';

    const { data: overallMetrics, isLoading: overallLoading } = useOverallMetrics(isSystemSelected ? geoCode : '', espShortCode);
    const { data: espPerformance, isLoading: espLoading } = useEspPerformance(isSystemSelected ? geoCode : '', dateRange.days, espShortCode);
    const { data: tagDistribution, isLoading: tagsLoading } = useTagDistribution(isSystemSelected ? geoCode : '', espShortCode);
    const { data: campaigns, isLoading: campaignsLoading } = useCampaignHistory(isSystemSelected ? geoCode : '', espShortCode, 5);

    // Filtered ESPs based on selected country
    const filteredEsps = systems.filter((s) => s.country === selectedCountry);

    // Fetch newsletter data when ESP is selected
    useEffect(() => {
        if (!selectedEsp) {
            setNewsletters([]);
            setChartData([]);
            return;
        }

        const fetchNewsletterData = async () => {
            setLoading(true);
            try {
                const selectedSystem = systems.find(s => s.id === selectedEsp);
                if (!selectedSystem) return;

                const geo = selectedSystem.country.toLowerCase();
                const espCode = selectedSystem.esp === 'mailerlite' ? 'ml' : selectedSystem.esp === 'moosend' ? 'ms' : selectedSystem.esp === 'drip' ? 'dp' : selectedSystem.esp === 'brevo' ? 'br' : 'gr';

                // Fetch recent campaigns from internal API
                const data = await dashboardApi.getCampaignHistory(geo, espCode, 30);

                if (data.campaigns && Array.isArray(data.campaigns)) {
                    const processedData: NewsletterStats[] = data.campaigns.map((campaign: any) => {
                        const sendTime = new Date(campaign.send_date);
                        const now = new Date();
                        const hoursSinceSend = (now.getTime() - sendTime.getTime()) / (1000 * 60 * 60);

                        // Internal API returns numbers directly
                        const recipients = campaign.total_delivered || 0;
                        const openRateValue = campaign.open_rate || 0;
                        const uniqueOpened = campaign.unique_opens || Math.round(recipients * openRateValue);

                        let estimatedOpenRate: number | undefined;
                        if (hoursSinceSend < 72 && hoursSinceSend > 0) {
                            estimatedOpenRate = estimateFinalOpenRate(hoursSinceSend, openRateValue, 72);
                        }

                        return {
                            newsletterId: campaign.newsletter_id || campaign.campaign_id,
                            name: campaign.campaign_name || 'Unnamed',
                            sendOn: campaign.send_date,
                            recipients,
                            uniqueOpened,
                            openRate: openRateValue,
                            hoursSinceSend,
                            estimatedOpenRate
                        };
                    });

                    // Sort by date descending
                    processedData.sort((a, b) => new Date(b.sendOn).getTime() - new Date(a.sendOn).getTime());

                    setNewsletters(processedData);
                    processChartData(processedData);
                }
            } catch (error) {
                console.error('Failed to fetch newsletter data:', error);
                setNewsletters([]);
                setChartData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchNewsletterData();
    }, [selectedEsp]);

    /**
     * Process newsletters into chart data points
     * Groups by date and averages if multiple sends per day
     */
    const processChartData = (newsletters: NewsletterStats[]) => {
        const grouped = new Map<string, NewsletterStats[]>();

        newsletters.forEach(nl => {
            const date = format(parseISO(nl.sendOn), 'yyyy-MM-dd');
            if (!grouped.has(date)) {
                grouped.set(date, []);
            }
            grouped.get(date)!.push(nl);
        });

        const chartPoints: ChartDataPoint[] = [];

        grouped.forEach((nls, date) => {
            const avgActualOpens = nls.reduce((sum, nl) => sum + nl.uniqueOpened, 0) / nls.length;
            const isRecent = nls.some(nl => nl.hoursSinceSend < 72);

            let avgEstimatedTotalOpens = avgActualOpens;
            if (isRecent) {
                const estimatedRates = nls.map(nl => {
                    if (nl.estimatedOpenRate !== undefined) {
                        return nl.estimatedOpenRate * nl.recipients;
                    }
                    return nl.uniqueOpened;
                });
                avgEstimatedTotalOpens = estimatedRates.reduce((sum, val) => sum + val, 0) / nls.length;
            }

            const estimatedAddition = Math.max(0, avgEstimatedTotalOpens - avgActualOpens);

            chartPoints.push({
                date,
                actualOpens: Math.round(avgActualOpens),
                estimatedOpens: Math.round(estimatedAddition),
                totalEstimated: Math.round(avgEstimatedTotalOpens),
                isRecent
            });
        });

        chartPoints.sort((a, b) => a.date.localeCompare(b.date));
        setChartData(chartPoints);
    };

    const handleCountryChange = (value: string) => {
        setSelectedCountry(value);
        setSelectedEsp('');
        setNewsletters([]);
        setChartData([]);
    };

    const handleUpdateData = async () => {
        if (!selectedEsp) return;

        setIsUpdating(true);
        setUpdateStatus('Downloading...');

        try {
            console.log('Triggering download_segments for', selectedEsp);
            await dashboardApi.triggerOp('download_segments', { esp_id: selectedEsp });

            setUpdateStatus('Syncing...');
            console.log('Triggering sync_engagement for', selectedEsp);
            await dashboardApi.triggerOp('sync_engagement', { esp_id: selectedEsp });

            setUpdateStatus('Done!');

            setTimeout(() => {
                setUpdateStatus('');
                alert("Data update complete. The charts will reflect new data shortly.");
            }, 1000);

        } catch (error: any) {
            console.error('Update failed:', error);
            const msg = error.message || String(error);

            if (msg.includes('504') || msg.toLowerCase().includes('timeout')) {
                setUpdateStatus('Timeout');
                alert("The operation timed out in the browser but is likely still running in the background. Please check back in a few minutes.");
            } else {
                setUpdateStatus('Error');
                alert(`Update failed: ${msg}`);
            }
        } finally {
            setIsUpdating(false);
            if (updateStatus !== 'Done!') {
                setTimeout(() => setUpdateStatus(''), 3000);
            }
        }
    };

    return (
        <MainLayout>
            {/* Header with Legacy Toggle (Legacy tabs & selectors hidden by default) */}
            {showLegacyTabs ? (
                <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                    <div className="max-w-7xl mx-auto px-6 py-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-bold flex items-center gap-2">
                                    📊 ESP Dashboard
                                </h1>
                                <button
                                    onClick={() => {
                                        setShowLegacyTabs(false);
                                        setActiveTab('kpis');
                                    }}
                                    className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                >
                                    Hide legacy tabs
                                </button>
                            </div>

                            <div className="flex flex-wrap items-center gap-4">
                                {/* Country Selector */}
                                <div className="flex flex-col">
                                    <label className="text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Country</label>
                                    <Select value={selectedCountry} onValueChange={handleCountryChange}>
                                        <SelectTrigger
                                            className="w-40 border border-gray-300 shadow-sm rounded-md px-3 py-2 text-sm flex items-center justify-between bg-white hover:border-blue-400 transition-colors"
                                        >
                                            <div className="flex items-center gap-2">
                                                <img
                                                    src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                                                    alt={selectedCountry}
                                                    className="h-3.5 w-5 rounded-sm object-cover"
                                                />
                                                <SelectValue>
                                                    {countries.find((c) => c.id === selectedCountry)?.label}
                                                </SelectValue>
                                            </div>
                                        </SelectTrigger>
                                        <SelectContent position="popper" className="z-[100] w-40 border border-gray-300 rounded-md bg-white shadow-lg">
                                            {countries.map((c) => (
                                                <SelectItem
                                                    key={c.id}
                                                    value={c.id}
                                                    className="px-3 py-2 hover:bg-blue-50 text-sm cursor-pointer"
                                                >
                                                    {c.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* ESP Selector */}
                                <div className="flex flex-col">
                                    <label className="text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">ESP System</label>
                                    <select
                                        className="h-9 w-64 border border-gray-300 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-400 transition-colors cursor-pointer"
                                        value={selectedEsp}
                                        onChange={(e) => setSelectedEsp(e.target.value)}
                                    >
                                        <option value="" disabled>Select ESP</option>
                                        {filteredEsps.map((esp) => (
                                            <option key={esp.id} value={esp.id}>
                                                {esp.label} ({esp.country})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Update Data Button */}
                                <div className="flex flex-col justify-end">
                                    <label className="text-[10px] uppercase font-bold text-transparent mb-1 ml-1">Action</label>
                                    <button
                                        onClick={handleUpdateData}
                                        disabled={isUpdating || !selectedEsp}
                                        className={`h-9 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center min-w-[120px] ${isUpdating || !selectedEsp
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                                            : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 hover:border-blue-300 shadow-sm'
                                            }`}
                                    >
                                        {isUpdating ? (
                                            <span className="flex items-center gap-2">
                                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {updateStatus || 'Updating...'}
                                            </span>
                                        ) : (updateStatus === 'Done!' ? '✅ Done!' : 'Update Data')}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="flex gap-1 border-b border-gray-200">
                            <button
                                onClick={() => setActiveTab('kpis')}
                                className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${activeTab === 'kpis'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                🎯 KPI Command Center
                            </button>
                            <button
                                onClick={() => setActiveTab('performance')}
                                className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${activeTab === 'performance'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                📈 Performance Metrics (Legacy)
                            </button>
                            <button
                                onClick={() => setActiveTab('rules')}
                                className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${activeTab === 'rules'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                ⚙️ Segmentation Rules (Legacy)
                            </button>
                            <button
                                onClick={() => setActiveTab('deliverability')}
                                className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${activeTab === 'deliverability'
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                🛡️ Deliverability (Legacy)
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto px-6 pt-2 flex justify-end">
                    <button
                        onClick={() => setShowLegacyTabs(true)}
                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        Show legacy tabs (Deliverability / Rules / RDS)
                    </button>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-6 pb-12 mt-4">
                {/* Multi-ESP KPI Command Center Tab */}
                {activeTab === 'kpis' && (
                    <EspKpiCommandCenter
                        onSelectEspForDeepDive={(country, espId) => {
                            setSelectedCountry(country);
                            setSelectedEsp(espId);
                            setShowLegacyTabs(true);
                            setActiveTab('performance');
                        }}
                    />
                )}

                {/* Deliverability Tab (all ESPs and sending domains) */}
                {activeTab === 'deliverability' && <DeliverabilityPanel />}

                {/* Segmentation Rules Tab */}
                {activeTab === 'rules' && (
                    <SegmentationRulesConfig
                        selectedEsp={selectedEsp}
                        geo={geoCode}
                    />
                )}

                {/* Performance Tab */}
                {activeTab === 'performance' && (
                    <>
                        {!selectedEsp ? (
                            /* Empty state when no ESP is selected */
                            <div className="flex flex-col items-center justify-center p-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                <div className="text-gray-400 mb-4">
                                    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900">Select an ESP to view performance</h3>
                                <p className="text-gray-500 text-center max-w-sm mt-1">
                                    Choose a country and ESP system from the dropdowns above to load performance metrics and charts.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* ISP Performance Charts */}
                                <IspPerformanceCharts geo={geoCode} />

                                {/* RDS Metrics Section */}
                                {isSystemSelected && (
                                    <div className="space-y-6 mb-8">
                                        {/* Overall Metrics Card */}
                                        <Card className="shadow-sm border-gray-200">
                                            <CardHeader className="pb-2">
                                                <h2 className="text-xl font-semibold">Overall Database Metrics</h2>
                                                <p className="text-xs text-gray-500">Real-time stats from PostgreSQL RDS (Norway)</p>
                                            </CardHeader>
                                            <CardContent>
                                                {overallLoading ? (
                                                    <div className="py-4 text-center text-gray-500">Loading metrics...</div>
                                                ) : overallMetrics ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                                            <p className="text-xs text-gray-600 font-medium mb-1">Total Subscribers</p>
                                                            <p className="text-2xl font-bold text-blue-600">{overallMetrics.total_subscribers.toLocaleString()}</p>
                                                            <p className="text-[10px] text-gray-500 mt-1">{overallMetrics.active_subscribers} active</p>
                                                        </div>
                                                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                                                            <p className="text-xs text-gray-600 font-medium mb-1">Open Rate</p>
                                                            <p className="text-2xl font-bold text-purple-600">
                                                                {(overallMetrics.overall_open_rate * 100).toFixed(1)}%
                                                            </p>
                                                            <p className="text-[10px] text-gray-500 mt-1">Global average</p>
                                                        </div>
                                                        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                                                            <p className="text-xs text-gray-600 font-medium mb-1">Click Rate</p>
                                                            <p className="text-2xl font-bold text-green-600">
                                                                {(overallMetrics.overall_click_rate * 100).toFixed(1)}%
                                                            </p>
                                                            <p className="text-[10px] text-gray-500 mt-1">Global CTR</p>
                                                        </div>
                                                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                                                            <p className="text-xs text-gray-600 font-medium mb-1">Campaigns (30d)</p>
                                                            <p className="text-2xl font-bold text-orange-600">{overallMetrics.active_campaigns_30d}</p>
                                                            <p className="text-[10px] text-gray-500 mt-1">Active last month</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-red-500 py-4 text-center text-sm font-medium">Failed to load metrics from RDS</p>
                                                )}
                                            </CardContent>
                                        </Card>

                                        {/* ESP Performance Card */}
                                        <Card className="shadow-sm border-gray-200">
                                            <CardHeader className="pb-2">
                                                <h2 className="text-xl font-semibold">ESP & ISP Performance</h2>
                                                <p className="text-xs text-gray-500">Reputation scores and engagement by provider</p>
                                            </CardHeader>
                                            <CardContent>
                                                {espLoading ? (
                                                    <div className="py-4 text-center text-gray-500">Loading ESP performance...</div>
                                                ) : espPerformance ? (
                                                    <div className="space-y-4">
                                                        {espPerformance.esp_metrics.map((esp: EspMetric) => (
                                                            <div key={esp.esp_family} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                                                                <div className="flex justify-between items-center mb-2">
                                                                    <h3 className="text-sm font-bold capitalize text-gray-800">{esp.esp_family}</h3>
                                                                    <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded text-gray-600">{esp.subscriber_count.toLocaleString()} subs</span>
                                                                </div>
                                                                <div className="grid grid-cols-3 gap-4 text-xs">
                                                                    <div>
                                                                        <span className="text-gray-500">Open Rate:</span>
                                                                        <span className="ml-1 font-semibold">{(esp.avg_open_rate * 100).toFixed(1)}%</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">Click Rate:</span>
                                                                        <span className="ml-1 font-semibold">{(esp.avg_click_rate * 100).toFixed(1)}%</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">Reputation:</span>
                                                                        <span className={`ml-1 font-bold ${esp.reputation_score > 70 ? 'text-green-600' : 'text-orange-600'}`}>
                                                                            {esp.reputation_score}/100
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-red-500 py-4 text-center text-sm font-medium">Failed to load ESP metrics</p>
                                                )}
                                            </CardContent>
                                        </Card>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Tag Distribution */}
                                            <Card className="shadow-sm border-gray-200">
                                                <CardHeader className="pb-2">
                                                    <h2 className="text-xl font-semibold">Subscriber Tag Distribution</h2>
                                                </CardHeader>
                                                <CardContent>
                                                    {tagsLoading ? (
                                                        <div className="py-4 text-center text-gray-500">Loading tags...</div>
                                                    ) : tagDistribution ? (
                                                        <div className="space-y-3">
                                                            {tagDistribution.tag_metrics.map((tag: TagMetric) => (
                                                                <div key={tag.tag} className="flex justify-between items-center text-sm">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-semibold text-gray-700">{tag.tag}</span>
                                                                        <span className="text-[10px] text-gray-500">({tag.percentage}%)</span>
                                                                    </div>
                                                                    <div className="text-xs font-medium text-blue-600">
                                                                        {(tag.avg_open_rate * 100).toFixed(1)}% OR
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-red-500 py-4 text-center text-sm font-medium">Failed to load tags</p>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Recent Campaigns */}
                                            <Card className="shadow-sm border-gray-200">
                                                <CardHeader className="pb-2">
                                                    <h2 className="text-xl font-semibold">Recent RDS Campaigns</h2>
                                                </CardHeader>
                                                <CardContent>
                                                    {campaignsLoading ? (
                                                        <div className="py-4 text-center text-gray-500">Loading campaigns...</div>
                                                    ) : campaigns ? (
                                                        <div className="space-y-3">
                                                            {campaigns.campaigns.map((campaign: Campaign) => (
                                                                <div key={campaign.campaign_id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                                                                    <p className="font-semibold text-xs text-gray-800 truncate">{campaign.campaign_name}</p>
                                                                    <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-500 mt-1">
                                                                        <div>Sent: {campaign.total_sent.toLocaleString()}</div>
                                                                        <div className="font-medium text-blue-600">OR: {(campaign.open_rate * 100).toFixed(1)}%</div>
                                                                        <div className="font-medium text-green-600">CTR: {(campaign.click_rate * 100).toFixed(1)}%</div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-red-500 py-4 text-center text-sm font-medium">Failed to load local campaigns</p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </div>

                                        {/* Connection Status Indicator */}
                                        <div className="flex justify-center items-center gap-2 py-2 text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                            Connected to RDS PostgreSQL via Lambda Proxy (Port 3001)
                                        </div>
                                    </div>
                                )}

                                {/* Chart Card */}
                                {selectedEsp && (
                                    <Card className="shadow-sm border-gray-200 overflow-hidden">
                                        <CardHeader className="px-10 pt-10 pb-4">
                                            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                                                Unique Opens Evolution
                                            </h2>
                                        </CardHeader>

                                        <CardContent className="px-10 pb-10 pt-2">
                                            {loading ? (
                                                <div className="flex items-center justify-center h-96">
                                                    <p className="text-gray-500">Loading data...</p>
                                                </div>
                                            ) : chartData.length === 0 ? (
                                                <div className="flex items-center justify-center h-96">
                                                    <p className="text-gray-500">No data available</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="mb-8 flex flex-wrap gap-8 text-sm bg-blue-50/50 p-5 rounded-xl border border-blue-100">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-3.5 h-3.5 bg-blue-600 rounded-full shadow-sm"></div>
                                                            <span className="font-semibold text-gray-700">Actual Opens</span>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-3.5 h-3.5 bg-blue-200 rounded-full shadow-sm"></div>
                                                            <span className="font-semibold text-gray-700">Estimated Additional Opens (&lt; 72h)</span>
                                                        </div>
                                                    </div>

                                                    <ResponsiveContainer width="100%" height={400}>
                                                        <AreaChart data={chartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis
                                                                dataKey="date"
                                                                tickFormatter={(date) => format(parseISO(date), 'MMM dd')}
                                                            />
                                                            <YAxis label={{ value: 'Unique Opens', angle: -90, position: 'insideLeft' }} />
                                                            <Tooltip
                                                                content={({ active, payload }) => {
                                                                    if (active && payload && payload.length) {
                                                                        const data = payload[0].payload as ChartDataPoint;
                                                                        return (
                                                                            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                                                                <p className="font-semibold">{format(parseISO(data.date), 'MMM dd, yyyy')}</p>
                                                                                <p className="text-blue-600">Actual Opens: {data.actualOpens}</p>
                                                                                {data.isRecent && (
                                                                                    <>
                                                                                        <p className="text-blue-300">Estimated Additional: {data.estimatedOpens}</p>
                                                                                        <p className="text-gray-700 font-semibold">Total Estimated (72h): {data.totalEstimated}</p>
                                                                                    </>
                                                                                )}
                                                                                {data.isRecent && (
                                                                                    <p className="text-xs text-gray-500 mt-1">* Sent within 72 hours</p>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return null;
                                                                }}
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="actualOpens"
                                                                stackId="1"
                                                                stroke="#3b82f6"
                                                                fill="#3b82f6"
                                                                name="Actual Opens"
                                                            />
                                                            <Area
                                                                type="monotone"
                                                                dataKey="estimatedOpens"
                                                                stackId="1"
                                                                stroke="#bfdbfe"
                                                                fill="#bfdbfe"
                                                                name="Estimated Additional"
                                                            />
                                                        </AreaChart>
                                                    </ResponsiveContainer>

                                                    <div className="mt-6 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                                        <p>
                                                            <strong>Note:</strong> For campaigns sent less than 72 hours ago,
                                                            the chart shows estimated total unique opens at 72 hours using an
                                                            exponential growth model calibrated on historical data.
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </MainLayout>
    );
}
